import { useEffect, useRef, useState, useCallback } from 'react';
import { motion, useMotionValue, useSpring } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

function BouncingBall({ label, color, startX, startY, vxInit, vyInit, otherPos, onCollide, onClick }) {
  const posRef = useRef({ x: startX, y: startY });
  const velRef = useRef({ vx: vxInit, vy: vyInit });
  const x = useMotionValue(startX);
  const y = useMotionValue(startY);
  const springX = useSpring(x, { stiffness: 300, damping: 20 });
  const springY = useSpring(y, { stiffness: 300, damping: 20 });
  const frameRef = useRef(null);

  const animate = useCallback(() => {
    const pos = posRef.current;
    const vel = velRef.current;
    const w = window.innerWidth - 56;
    const h = window.innerHeight - 56;

    pos.x += vel.vx;
    pos.y += vel.vy;

    // Wall bounce
    if (pos.x <= 0 || pos.x >= w) { vel.vx *= -1; pos.x = Math.max(0, Math.min(w, pos.x)); }
    if (pos.y <= 80 || pos.y >= h) { vel.vy *= -1; pos.y = Math.max(80, Math.min(h, pos.y)); }

    // Ball-ball collision
    if (otherPos.current) {
      const ox = otherPos.current.x;
      const oy = otherPos.current.y;
      const dx = pos.x - ox;
      const dy = pos.y - oy;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < 70 && dist > 0) {
        const nx = dx / dist;
        const ny = dy / dist;
        vel.vx = nx * 2.5;
        vel.vy = ny * 2.5;
        pos.x += nx * 5;
        pos.y += ny * 5;
        if (onCollide) onCollide();
      }
    }

    x.set(pos.x);
    y.set(pos.y);
    frameRef.current = requestAnimationFrame(animate);
  }, [x, y, otherPos, onCollide]);

  useEffect(() => {
    frameRef.current = requestAnimationFrame(animate);
    return () => { if (frameRef.current) cancelAnimationFrame(frameRef.current); };
  }, [animate]);

  // Expose position
  useEffect(() => {
    const interval = setInterval(() => {
      if (otherPos && otherPos._self) {
        otherPos._self.current = posRef.current;
      }
    }, 16);
    return () => clearInterval(interval);
  }, [otherPos]);

  return (
    <motion.div
      className="fixed z-[9990] pointer-events-none"
      style={{ x: springX, y: springY }}
    >
      <motion.button
        onClick={onClick}
        className="pointer-events-auto cursor-pointer select-none"
        whileHover={{ scale: 1.2 }}
        whileTap={{ scale: 0.9 }}
      >
        <div
          className="w-14 h-14 rounded-full flex items-center justify-center text-[10px] font-black text-white shadow-lg"
          style={{ background: color, boxShadow: `0 0 30px ${color}44` }}
        >
          {label}
        </div>
      </motion.button>
    </motion.div>
  );
}

export default function BouncingButtons() {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [sparkle, setSparkle] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const pos1 = useRef({ x: 100, y: 200 });
  const pos2 = useRef({ x: 300, y: 400 });

  pos1._self = useRef(pos1.current);
  pos2._self = useRef(pos2.current);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    const handleScroll = () => setVisible(window.scrollY > 300);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => { window.removeEventListener('scroll', handleScroll); window.removeEventListener('resize', checkMobile); };
  }, []);

  const handleCollide = () => {
    setSparkle(true);
    setTimeout(() => setSparkle(false), 500);
  };

  if (!visible || isMobile) return null;

  return (
    <>
      <BouncingBall
        label="Enquiry"
        color="linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary-end)))"
        startX={Math.random() * 400 + 100}
        startY={Math.random() * 300 + 150}
        vxInit={1.8}
        vyInit={1.5}
        otherPos={pos2._self}
        onCollide={handleCollide}
        onClick={() => navigate('/enquiry')}
      />
      <BouncingBall
        label="Products"
        color="linear-gradient(135deg, #10b981, #047857)"
        startX={Math.random() * 400 + 500}
        startY={Math.random() * 300 + 200}
        vxInit={-1.6}
        vyInit={1.8}
        otherPos={pos1._self}
        onCollide={handleCollide}
        onClick={() => navigate('/products')}
      />
      {sparkle && (
        <div className="fixed inset-0 z-[9989] pointer-events-none">
          <div className="absolute animate-ping" style={{ left: '50%', top: '50%', width: 20, height: 20, borderRadius: '50%', background: 'hsl(var(--primary) / 0.3)' }}></div>
        </div>
      )}
    </>
  );
}
